import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { Member, StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { hasCustomAccess } from '../api/mappers';

import { MemberStatusBadge } from './member-status-badge';
import { StoreAccess } from './store-access';

export interface MemberColumnsOptions {
  /** Signed-in user's id, to mark their own row. */
  selfId: string | undefined;
  stores: readonly StaffProfileStoresItem[];
  /** Opens the explanation of what the member can do where. */
  onShowAccess?: (member: Member) => void;
}

/**
 * Column model for the members table. Sortable by name, role and last sign-in; the email
 * column starts hidden and is there so search matches it.
 */
export function useMemberColumns({ selfId, stores, onShowAccess }: MemberColumnsOptions) {
  const { t } = useTranslation('team');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<Member>>>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => row.user.name,
        header: t('columns.member'),
        meta: { sortField: 'name', lockVisibility: true },
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate font-medium">
              {row.original.user.name}
              {row.original.user.id === selfId ? (
                <span className="text-muted-foreground ml-1.5 text-xs font-normal">
                  ({t('you')})
                </span>
              ) : null}
            </span>
            <span className="text-muted-foreground truncate text-xs">
              {row.original.user.email}
            </span>
          </div>
        ),
      },
      {
        id: 'email',
        accessorFn: (row) => row.user.email,
        header: t('columns.email'),
        enableSorting: false,
      },
      {
        id: 'role',
        accessorFn: (row) => row.role.name,
        header: t('columns.role'),
        meta: { sortField: 'role', filterKey: 'role' },
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <span>{row.original.role.name}</span>
            {hasCustomAccess(row.original) ? (
              onShowAccess ? (
                <button
                  type="button"
                  data-tour="org.team.custom-access"
                  onClick={() => onShowAccess(row.original)}
                  aria-label={t('customAccess.open', { name: row.original.user.name })}
                >
                  <Badge variant="outline">{t('customAccess.badge')}</Badge>
                </button>
              ) : (
                <Badge variant="outline">{t('customAccess.badge')}</Badge>
              )
            ) : null}
          </span>
        ),
      },
      {
        id: 'access',
        header: t('columns.access'),
        enableSorting: false,
        cell: ({ row }) => <StoreAccess storeIds={row.original.storeIds} stores={stores} />,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        enableSorting: false,
        meta: { filterKey: 'status', searchable: false },
        cell: ({ row }) => <MemberStatusBadge member={row.original} />,
      },
      {
        id: 'lastLoginAt',
        accessorFn: (row) => row.user.lastLoginAt ?? undefined,
        header: t('columns.lastLoginAt'),
        sortUndefined: 'last',
        meta: { sortField: 'lastLoginAt', searchable: false },
        cell: ({ row }) =>
          row.original.user.lastLoginAt ? (
            <span
              className="text-muted-foreground"
              title={format.utc(row.original.user.lastLoginAt)}
            >
              {format.relative(row.original.user.lastLoginAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('neverSignedIn')}</span>
          ),
      },
    ],
    [format, onShowAccess, selfId, stores, t],
  );
}
